import { apiFetch } from "./api";

export interface QueuedMutation {
  id: string;
  path: string;
  method: "POST" | "PATCH" | "PUT";
  body: unknown;
  createdAt: string;
  attempts: number;
}

const DB_NAME = "vzlive-offline";
const STORE = "mutations";

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export async function queueMutation(
  mutation: Omit<QueuedMutation, "createdAt" | "attempts">,
) {
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).put({
      ...mutation,
      createdAt: new Date().toISOString(),
      attempts: 0,
    });
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  db.close();
  window.dispatchEvent(new CustomEvent("vzlive:queue-change"));
}

export async function listQueuedMutations(): Promise<QueuedMutation[]> {
  const db = await openDatabase();
  const values = await new Promise<QueuedMutation[]>((resolve, reject) => {
    const request = db.transaction(STORE).objectStore(STORE).getAll();
    request.onsuccess = () => resolve(request.result as QueuedMutation[]);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return values.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function deleteMutation(id: string) {
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  db.close();
}

export async function flushQueue() {
  if (!navigator.onLine)
    return { sent: 0, remaining: (await listQueuedMutations()).length };
  const mutations = await listQueuedMutations();
  let sent = 0;

  for (const mutation of mutations) {
    try {
      await apiFetch(mutation.path, {
        method: mutation.method,
        body: JSON.stringify(mutation.body),
      });
      await deleteMutation(mutation.id);
      sent += 1;
    } catch {
      break;
    }
  }
  const remaining = (await listQueuedMutations()).length;
  window.dispatchEvent(new CustomEvent("vzlive:queue-change"));
  return { sent, remaining };
}
